import { useCallback, useState } from 'react';

/**
 * useSessionState — a drop-in replacement for React's useState whose value is
 * persisted to sessionStorage and restored on mount.
 *
 * WHY THIS EXISTS
 * ----------------
 * Route components unmount when the user navigates away, wiping their local
 * useState. When you want a piece of view state (filters, sort order, an open
 * tab, etc.) to survive that — but only for the current browser-tab session —
 * this hook persists it transparently. It behaves exactly like useState in
 * every other respect, including functional updates: setValue(prev => next).
 *
 * WHY sessionStorage (not localStorage)
 * -------------------------------------
 *   - Scoped to the tab and auto-cleared when the tab closes, so it's a natural
 *     fit for "current session" state rather than long-lived preferences.
 *   - Not shared across tabs, so two tabs can hold independent view state.
 * For durable cross-session preferences, use a localStorage-based approach
 * instead (e.g. useAccessibilityPrefs).
 *
 * DATE-AWARE SERIALIZATION
 * ------------------------
 * Plain JSON.stringify converts Date objects to ISO strings that can't be
 * reliably told apart from ordinary strings on the way back in. This hook tags
 * Dates as { __date: <ms> } on write and revives them on read, so nested Date
 * values (e.g. date-range filters) round-trip as real Date instances. All other
 * JSON-safe values (strings, numbers, booleans, null, arrays, plain objects
 * such as react-select { value, label } options) pass through untouched.
 *
 * RESILIENCE
 * ----------
 * Every storage access is wrapped in try/catch because sessionStorage can throw
 * (private mode, quota, disabled storage, or SSR where it's undefined). On any
 * failure the hook degrades gracefully to plain in-memory state for the
 * lifetime of the component.
 *
 * SCHEMA CHANGES
 * --------------
 * The persisted value's shape is the caller's contract. If you change that
 * shape incompatibly, namespace your key with a version segment and bump it
 * (see DASHBOARD_KEYS) so stale entries from older builds are ignored rather
 * than mis-read.
 *
 * @template T
 * @param {string} key            sessionStorage key (use a namespaced constant).
 * @param {T | (() => T)} initialValue  Value, or lazy initializer, used when
 *                                      nothing valid is stored yet.
 * @returns {[T, (value: T | ((prev: T) => T)) => void]} A [value, setValue] pair.
 */
export default function useSessionState(key, initialValue) {
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw != null) {
        return JSON.parse(raw, dateReviver);
      }
    } catch (_) {
      // Ignore and fall back to the initial value.
    }
    return typeof initialValue === 'function' ? initialValue() : initialValue;
  });

  const setAndPersist = useCallback((value) => {
    setState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      try {
        sessionStorage.setItem(key, JSON.stringify(next, dateReplacer));
      } catch (_) {
        // Non-fatal: keep the in-memory value even if persistence fails.
      }
      return next;
    });
  }, [key]);

  return [state, setAndPersist];
}

// JSON.stringify replacer that preserves Date objects. By the time the replacer
// is called, `value` has already been through Date.prototype.toJSON (an ISO
// string), so we read the ORIGINAL value off `this[key]` to detect the Date —
// this is the standard trick and relies on a non-arrow function for `this`.
function dateReplacer(key, value) {
  const original = this[key];
  if (original instanceof Date) {
    return { __date: original.getTime() };
  }
  return value;
}

// JSON.parse reviver that turns our { __date: <ms> } tags back into Date
// objects. The tag shape is unique to this hook, so it can't collide with other
// stored object shapes (e.g. react-select { value, label } options).
function dateReviver(key, value) {
  if (value && typeof value === 'object' && typeof value.__date === 'number') {
    return new Date(value.__date);
  }
  return value;
}
